import { FC, useId, useRef } from 'react'
import cx from 'classnames'
import { YouTubeProps } from 'react-youtube'
import {
  MediaPlayer,
  MediaProvider,
  Controls,
  PlayButton,
  MediaProviderAdapter,
  isYouTubeProvider,
  VolumeSlider,
  Time,
  MuteButton,
  TimeSlider,
  FullscreenButton,
  useMediaState,
  MediaPlayerInstance,
} from '@vidstack/react'
import { ITooltip } from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import { PictureProps } from '../Picture'
import { Icons } from '../Icon'
import { colors } from '../../theme'
import { TranscriptProps } from '../Transcript'

import * as SC from './styled'

export type VideoYoutubeProps = {
  className?: string
  options?: YouTubeProps
  cover?: PictureProps
  texts: {
    play?: string
    playVideo?: string
    pause?: string
    skip?: string
    mute?: string
    unmute?: string
    transcript?: string
    fullscreen?: string
    exitFullscreen?: string
  }
  handleSkip?: () => void
  handleTranscript?: () => void
  color?: string
  transcriptProps?: TranscriptProps
}

const PlayerToolTip = (props: ITooltip) => {
  return (
    <SC._Tooltip
      openEvents={{ mouseenter: true, focus: true }}
      closeEvents={{ mouseleave: true, blur: true }}
      globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
      positionStrategy="fixed"
      style={{ zIndex: 500 }}
      {...props}
      offset={20}
      noArrow
    />
  )
}

const VideoYoutube: FC<VideoYoutubeProps> = ({
  className,
  options,
  cover,
  texts = {},
  handleSkip,
  handleTranscript,
  color = colors.blueLight,
}) => {
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language
  const onProviderChange = (provider: MediaProviderAdapter | null) => {
    if (isYouTubeProvider(provider)) {
      provider.cookies = false
      provider.color = 'white'
      provider.language = currentLanguage
    }
  }
  const player = useRef<MediaPlayerInstance>(null)
  const id = useId()

  const isFullscreen = useMediaState('fullscreen', player)
  const isPlaying = !useMediaState('paused', player)
  const isMuted = useMediaState('muted', player)
  const volume = useMediaState('volume', player)
  const started = useMediaState('started', player)

  return options?.videoId ? (
    <>
      <SC.Container className={cx('VideoYoutube', className)} $color={color}>
        <MediaPlayer
          title={options?.title}
          src={`youtube/${options.videoId}`}
          onProviderChange={onProviderChange}
          load="idle"
          playsInline={true}
          className="vs-player"
          ref={player}
        >
          <MediaProvider className="vs-provider" />
          <Controls.Root className="vs-controls">
            {cover && (
              <Controls.Group className="vs-controls__cover">
                <PlayButton className="vs-cover">
                  <SC.Cover aria-controls={id} hidden={started}>
                    <SC.Play $color={color}>
                      <SC._Icon icon={Icons.playerPlay} />
                      {texts?.playVideo && (
                        <SC.Label>{texts?.playVideo}</SC.Label>
                      )}
                    </SC.Play>
                    <SC._Picture {...cover} />
                  </SC.Cover>
                </PlayButton>
              </Controls.Group>
            )}
            <Controls.Group className="vs-controls__bottom">
              <PlayButton
                className="vs-play"
                id={`${id}__play`}
                aria-labelledby={`${id}__label--play`}
              >
                {isPlaying ? (
                  <SC._Icon icon={Icons.playerPause} title={texts.pause} />
                ) : (
                  <SC._Icon icon={Icons.playerPlay} title={texts.play} />
                )}
              </PlayButton>
              {handleSkip && (
                <SC.Skip
                  type="button"
                  id={`${id}__skip`}
                  aria-labelledby={`${id}__label--skip`}
                  onClick={handleSkip}
                >
                  <SC._Icon icon={Icons.playerSkipForward} title={texts.skip} />
                </SC.Skip>
              )}
              <SC.__Time>
                <Time type="current" />
                <div>/</div>
                <Time type="duration" />
              </SC.__Time>
              <TimeSlider.Root className="vs-progress">
                <TimeSlider.Track className="vs-progress__track">
                  <TimeSlider.TrackFill className="vs-progress__fill" />
                  <TimeSlider.Progress className="vs-progress__progress" />
                </TimeSlider.Track>
              </TimeSlider.Root>
              <SC.__Volume>
                <MuteButton
                  className="vs-mute"
                  id={`${id}__mute`}
                  aria-labelledby={`${id}__label--mute`}
                >
                  {isMuted || volume <= 0.01 ? (
                    <SC._Icon
                      icon={Icons.playerVolumeMute}
                      title={texts.unmute}
                    />
                  ) : volume <= 0.5 ? (
                    <SC._Icon icon={Icons.playerVolume1} title={texts.mute} />
                  ) : (
                    <SC._Icon icon={Icons.playerVolume2} title={texts.mute} />
                  )}
                </MuteButton>
                <VolumeSlider.Root className="vs-volume">
                  <VolumeSlider.Track className="vs-volume__track">
                    <VolumeSlider.TrackFill className="vs-volume__fill" />
                  </VolumeSlider.Track>
                  <VolumeSlider.Thumb className="vs-volume__thumb" />
                </VolumeSlider.Root>
              </SC.__Volume>
              {/* Captions not detected from Youtube */}
              {/* 
                <CaptionButton>
                  <span>ON</span>
                  <span>OFF</span>
                </CaptionButton> 
              */}
              {handleTranscript && (
                <SC.Transcript
                  type="button"
                  onClick={handleTranscript}
                  id={`${id}__transcript`}
                  aria-labelledby={`${id}__label--transcript`}
                >
                  <SC._Icon
                    icon={Icons.playerTranscript}
                    title={texts.transcript}
                  />
                </SC.Transcript>
              )}
              <FullscreenButton
                className="vs-fullscreen"
                id={`${id}__fullscreen`}
                aria-labelledby={`${id}__label--fullscreen`}
              >
                {isFullscreen ? (
                  <SC._Icon
                    icon={Icons.playerMinimize}
                    title={texts.exitFullscreen}
                  />
                ) : (
                  <SC._Icon
                    icon={Icons.playerMaximize}
                    title={texts.fullscreen}
                  />
                )}
              </FullscreenButton>
            </Controls.Group>
            <Controls.Group className="vs-controls__progress">
              <TimeSlider.Root className="vs-progress is-alone">
                <TimeSlider.Track className="vs-progress__track">
                  <TimeSlider.TrackFill className="vs-progress__fill" />
                  <TimeSlider.Progress className="vs-progress__progress" />
                </TimeSlider.Track>
              </TimeSlider.Root>
            </Controls.Group>
          </Controls.Root>
        </MediaPlayer>

        {/* Tooltips here */}
        {/* Selector is dumb because anchorSelect doesn't seem to work with react-generated ids */}
        <PlayerToolTip
          anchorSelect={`[id*='${id}__play']`}
          id={`${id}__label--play`}
        >
          {isPlaying ? texts.pause : texts.play}
        </PlayerToolTip>
        <PlayerToolTip
          anchorSelect={`[id*='${id}__skip']`}
          id={`${id}__label--skip`}
        >
          {texts.skip}
        </PlayerToolTip>
        <PlayerToolTip
          anchorSelect={`[id*='${id}__mute']`}
          id={`${id}__label--mute`}
        >
          {isMuted || volume <= 0.01 ? texts.unmute : texts.mute}
        </PlayerToolTip>
        <PlayerToolTip
          anchorSelect={`[id*='${id}__transcript']`}
          id={`${id}__label--transcript`}
        >
          {texts.transcript}
        </PlayerToolTip>
        <PlayerToolTip
          anchorSelect={`[id*='${id}__fullscreen']`}
          id={`${id}__label--fullscreen`}
        >
          {isFullscreen ? texts.exitFullscreen : texts.fullscreen}
        </PlayerToolTip>
      </SC.Container>
    </>
  ) : null
}

export default VideoYoutube
